/**
 * FollowUpDetailView - Full-page view for follow-up details
 */

import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  Alert,
  Snackbar,
  CircularProgress,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  ArrowBack,
  Edit,
  CheckCircle,
  Delete,
  Message,
  MoreVert,
} from "@mui/icons-material";
import { WhatsApp } from "@mui/icons-material";
import { format } from "date-fns";
import {
  useFollowUp,
  useCompleteFollowUp,
  useDeleteFollowUp,
} from "../hooks/useFollowUps.js";
import FollowUpTemplateSelector from "../components/FollowUpTemplateSelector.jsx";
import { ConfigProvider } from "antd";
import { bubakiiTheme } from "../../../theme/bubakiiTheme.js";
import CustomerNotesPanel from "../../customers/components/CustomerNotesPanel.jsx";
import { openFollowUpWhatsAppLink, generateFollowUpWhatsAppLink } from "../utils/whatsappLinkGenerator.js";
import { getQuoteById } from "../../quotes/services/quoteService.js";
import useDeviceType from "../../leads/hooks/useDeviceType.js";

/**
 * FollowUpDetailView Component
 */
const FollowUpDetailView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: followUp, isLoading, error, refetch } = useFollowUp(id);
  const completeMutation = useCompleteFollowUp();
  const deleteMutation = useDeleteFollowUp();
  const { isMobile } = useDeviceType();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [templateModalVisible, setTemplateModalVisible] = useState(false);
  const [quote, setQuote] = useState(null);
  const [loadingQuote, setLoadingQuote] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "success";
      case "overdue":
        return "error";
      case "due":
        return "warning";
      case "pending":
        return "default";
      default:
        return "default";
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "urgent":
        return "error";
      case "high":
        return "warning";
      case "medium":
        return "info";
      case "low":
        return "default";
      default:
        return "default";
    }
  };

  const getEntityTypeColor = (entityType) => {
    switch (entityType) {
      case "quote":
        return "primary";
      case "customer":
        return "success";
      case "invoice":
        return "warning";
      case "order":
        return "info";
      case "lead":
        return "secondary";
      default:
        return "default";
    }
  };

  const handleComplete = async () => {
    if (window.confirm("Mark this follow-up as completed?")) {
      try {
        await completeMutation.mutateAsync({
          followUpId: id,
          followUpNotes: "",
        });
        setNotification({
          open: true,
          message: "Follow-up marked as completed",
          severity: "success",
        });
        refetch();
      } catch (error) {
        setNotification({
          open: true,
          message: error.message || "Failed to complete follow-up",
          severity: "error",
        });
      }
    }
  };

  const handleDelete = async () => {
    if (window.confirm("Are you sure you want to delete this follow-up?")) {
      try {
        await deleteMutation.mutateAsync(id);
        setNotification({
          open: true,
          message: "Follow-up deleted successfully",
          severity: "success",
        });
        setTimeout(() => navigate("/followups"), 1000);
      } catch (error) {
        setNotification({
          open: true,
          message: error.message || "Failed to delete follow-up",
          severity: "error",
        });
      }
    }
  };

  const handleEntityClick = () => {
    if (!followUp?.entityType || !followUp?.entityId) return;

    const entityRoutes = {
      quote: `/quotes/${followUp.entityId}`,
      customer: `/customers/${followUp.entityId}`,
      invoice: `/invoices/${followUp.entityId}`,
      order: `/orders/${followUp.entityId}`,
      lead: `/leads/${followUp.entityId}`,
    };

    const route = entityRoutes[followUp.entityType];
    if (route) {
      const isMobile =
        window.matchMedia("(max-width: 768px)").matches ||
        /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

      if (isMobile) {
        window.open(route, "_blank", "noopener,noreferrer");
        return;
      }

      window.open(route, "_blank", "noopener,noreferrer,width=1200,height=800");
    }
  };

  // Fetch quote data if this is a quote follow-up
  React.useEffect(() => {
    if (followUp?.entityType === "quote" && followUp?.entityId && !quote) {
      setLoadingQuote(true);
      getQuoteById(followUp.entityId)
        .then((response) => {
          if (response?.success !== false) {
            const quoteData =
              response?.data?.data ||
              response?.data ||
              response?.quote ||
              response ||
              null;
            if (quoteData) {
              setQuote(quoteData);
            }
          }
        })
        .catch((error) => {
          console.warn("Failed to fetch quote for follow-up:", error);
        })
        .finally(() => {
          setLoadingQuote(false);
        });
    }
  }, [followUp?.entityType, followUp?.entityId, quote]);

  const handleWhatsAppClick = () => {
    try {
      const quoteData = quote || null;
      const link = generateFollowUpWhatsAppLink(followUp, quoteData);
      
      if (!link) {
        setNotification({
          open: true,
          message: "Cannot generate WhatsApp link: phone number is missing or invalid. Please check the customer's phone number in the quote.",
          severity: "warning",
        });
        return;
      }
      
      openFollowUpWhatsAppLink(followUp, quoteData);
    } catch (error) {
      setNotification({
        open: true,
        message: error.message || "Failed to generate WhatsApp link",
        severity: "error",
      });
    }
  };

  const handleMenuOpen = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleMenuAction = (action) => {
    handleMenuClose();
    switch (action) {
      case "complete":
        handleComplete();
        break;
      case "edit":
        navigate(`/followups/${id}/edit`);
        break;
      case "template":
        setTemplateModalVisible(true);
        break;
      case "whatsapp":
        handleWhatsAppClick();
        break;
      case "delete":
        handleDelete();
        break;
      default:
        break;
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !followUp) {
    return (
      <Container>
        <Alert severity="error">
          {error?.message || "Follow-up not found"}
        </Alert>
      </Container>
    );
  }

  const isOverdue =
    followUp.status !== "completed" &&
    followUp.dueDate &&
    new Date(followUp.dueDate) < new Date();

  const mobileMenuItems =
    followUp.status !== "completed"
      ? [
          { key: "complete", label: "Complete", icon: <CheckCircle /> },
          ...(followUp.entityType === "quote"
            ? [
                {
                  key: "whatsapp",
                  label: "WhatsApp",
                  icon: <WhatsApp />,
                  disabled: loadingQuote,
                },
              ]
            : []),
          { key: "template", label: "Template", icon: <Message /> },
          { key: "edit", label: "Edit", icon: <Edit /> },
          { key: "delete", label: "Delete", icon: <Delete />, color: "error" },
        ]
      : [{ key: "delete", label: "Delete", icon: <Delete />, color: "error" }];

  return (
    <ConfigProvider theme={bubakiiTheme}>
      <Container 
        maxWidth="lg" 
        sx={{ 
          py: { xs: 2, sm: 3, md: 4 },
          px: { xs: 1.5, sm: 2, md: 3 },
          pb: { xs: followUp.status !== "completed" ? 10 : 6, md: 4 },
        }}
      >
        {/* Header Section - Mobile Optimized */}
        <Box sx={{ mb: { xs: 2, md: 3 } }}>
          {/* Back Button Row */}
          <Box sx={{ display: "flex", alignItems: "center", mb: { xs: 1.5, md: 2 } }}>
            <Button
              startIcon={<ArrowBack />}
              onClick={() => navigate("/followups")}
              size={isMobile ? "small" : "medium"}
              sx={{ 
                mr: { xs: 1, md: 2 },
                minWidth: { xs: "auto", md: "auto" },
              }}
            >
              {isSmallScreen ? "" : "Back"}
            </Button>
            {isMobile && followUp.status !== "completed" && (
              <Button
                variant="contained"
                color="success"
                startIcon={<CheckCircle />}
                onClick={handleComplete}
                size="small"
                sx={{ ml: "auto", mr: 1 }}
              >
                {isSmallScreen ? "" : "Complete"}
              </Button>
            )}
            {isMobile && (
              <IconButton
                onClick={handleMenuOpen}
                sx={{ ml: "auto" }}
                size="small"
              >
                <MoreVert />
              </IconButton>
            )}
          </Box>

          {/* Title Row */}
          <Box sx={{ 
            display: "flex", 
            alignItems: { xs: "flex-start", md: "center" },
            flexDirection: { xs: "column", md: "row" },
            gap: { xs: 1.5, md: 2 },
            mb: { xs: 2, md: 0 },
          }}>
            <Typography 
              variant={isMobile ? "h5" : "h4"} 
              sx={{ 
                flex: 1,
                wordBreak: "break-word",
                fontSize: { xs: "1.25rem", sm: "1.5rem", md: "2.125rem" },
                fontWeight: 600,
                lineHeight: { xs: 1.3, md: 1.2 },
              }}
            >
              {followUp.title || "Follow-up"}
            </Typography>
            
            {/* Desktop Action Buttons */}
            {!isMobile && (
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {followUp.status !== "completed" && (
                  <>
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<CheckCircle />}
                      onClick={handleComplete}
                    >
                      Complete
                    </Button>
                    {followUp.entityType === "quote" && (
                      <Button
                        variant="contained"
                        startIcon={<WhatsApp />}
                        onClick={handleWhatsAppClick}
                        disabled={loadingQuote}
                        sx={{
                          backgroundColor: "#25D366",
                          "&:hover": { backgroundColor: "#20BA5A" },
                        }}
                      >
                        WhatsApp
                      </Button>
                    )}
                    <Button
                      variant="outlined"
                      startIcon={<Message />}
                      onClick={() => setTemplateModalVisible(true)}
                    >
                      Template
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<Edit />}
                      onClick={() => navigate(`/followups/${id}/edit`)}
                    >
                      Edit
                    </Button>
                  </>
                )}
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<Delete />}
                  onClick={handleDelete}
                >
                  Delete
                </Button>
              </Box>
            )}
          </Box>

          {/* Mobile Action Menu */}
          <Menu
            anchorEl={menuAnchorEl}
            open={Boolean(menuAnchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
          >
            {followUp.status !== "completed" && (
              <>
                <MenuItem onClick={() => handleMenuAction("edit")}>
                  <Edit sx={{ mr: 1.5, fontSize: 20 }} />
                  Edit
                </MenuItem>
                <MenuItem onClick={() => handleMenuAction("template")}>
                  <Message sx={{ mr: 1.5, fontSize: 20 }} />
                  Template
                </MenuItem>
                {followUp.entityType === "quote" && (
                  <MenuItem 
                    onClick={() => handleMenuAction("whatsapp")}
                    disabled={loadingQuote}
                  >
                    <WhatsApp sx={{ mr: 1.5, fontSize: 20 }} />
                    WhatsApp
                  </MenuItem>
                )}
              </>
            )}
            <MenuItem onClick={() => handleMenuAction("delete")}>
              <Delete sx={{ mr: 1.5, fontSize: 20, color: "error.main" }} />
              <Typography color="error">Delete</Typography>
            </MenuItem>
          </Menu>
        </Box>

        {isOverdue && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: { xs: 2, md: 3 },
              fontSize: { xs: "0.875rem", md: "1rem" },
            }}
          >
            This follow-up is overdue!
          </Alert>
        )}

        <Grid container spacing={{ xs: 2, md: 3 }}>
          {/* Main Details */}
          <Grid item xs={12} md={8}>
            <Card sx={{ 
              boxShadow: { xs: 1, md: 2 },
            }}>
              <CardContent sx={{ 
                p: { xs: 2, sm: 2.5, md: 3 },
              }}>
                <Typography 
                  variant="h6" 
                  gutterBottom
                  sx={{
                    fontSize: { xs: "1.1rem", md: "1.25rem" },
                    fontWeight: 600,
                  }}
                >
                  Details
                </Typography>
                <Divider sx={{ mb: { xs: 1.5, md: 2 } }} />

                <Grid container spacing={{ xs: 1.5, md: 2 }}>
                  <Grid item xs={12}>
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ 
                        fontSize: { xs: "0.75rem", md: "0.875rem" },
                        mb: 0.5,
                      }}
                    >
                      Description
                    </Typography>
                    <Typography 
                      variant="body1"
                      sx={{
                        fontSize: { xs: "0.875rem", md: "1rem" },
                        wordBreak: "break-word",
                        lineHeight: { xs: 1.5, md: 1.6 },
                      }}
                    >
                      {followUp.description || "No description"}
                    </Typography>
                  </Grid>

                  {followUp.notes && (
                    <Grid item xs={12}>
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ 
                          fontSize: { xs: "0.75rem", md: "0.875rem" },
                          mb: 0.5,
                        }}
                      >
                        Notes
                      </Typography>
                      <Typography 
                        variant="body1"
                        sx={{
                          fontSize: { xs: "0.875rem", md: "1rem" },
                          wordBreak: "break-word",
                          lineHeight: { xs: 1.5, md: 1.6 },
                        }}
                      >
                        {followUp.notes}
                      </Typography>
                    </Grid>
                  )}

                  {followUp.followUpNotes && (
                    <Grid item xs={12}>
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ 
                          fontSize: { xs: "0.75rem", md: "0.875rem" },
                          mb: 0.5,
                        }}
                      >
                        Completion Notes
                      </Typography>
                      <Typography 
                        variant="body1"
                        sx={{
                          fontSize: { xs: "0.875rem", md: "1rem" },
                          wordBreak: "break-word",
                          lineHeight: { xs: 1.5, md: 1.6 },
                        }}
                      >
                        {followUp.followUpNotes}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>

            {/* Entity Context - Basic Info */}
            {followUp.entityType && followUp.entityId && (
              <Card sx={{ 
                mt: { xs: 2, md: 3 },
                boxShadow: { xs: 1, md: 2 },
              }}>
                <CardContent sx={{ 
                  p: { xs: 2, sm: 2.5, md: 3 },
                }}>
                  <Typography 
                    variant="h6" 
                    gutterBottom
                    sx={{
                      fontSize: { xs: "1.1rem", md: "1.25rem" },
                      fontWeight: 600,
                    }}
                  >
                    Related Entity
                  </Typography>
                  <Divider sx={{ mb: { xs: 1.5, md: 2 } }} />

                  <Box>
                    <Chip
                      label={followUp.entityType}
                      color={getEntityTypeColor(followUp.entityType)}
                      sx={{ textTransform: "capitalize", mb: 1 }}
                    />
                    {followUp.entityContext?.name && (
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        {followUp.entityContext.name}
                      </Typography>
                    )}
                    {followUp.entityContext?.phone && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1 }}
                      >
                        {followUp.entityContext.phone}
                      </Typography>
                    )}
                    {followUp.entityContext?.email && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1 }}
                      >
                        {followUp.entityContext.email}
                      </Typography>
                    )}
                    {followUp.entityContext?.referenceNumber && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1 }}
                      >
                        Ref: {followUp.entityContext.referenceNumber}
                      </Typography>
                    )}
                    {followUp.entityContext?.status && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1 }}
                      >
                        Status: {followUp.entityContext.status}
                      </Typography>
                    )}
                    <Button
                      variant="outlined"
                      size={isMobile ? "small" : "medium"}
                      onClick={handleEntityClick}
                      sx={{ 
                        mt: { xs: 1.5, md: 1 },
                        width: { xs: "100%", sm: "auto" },
                      }}
                    >
                      View Entity
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* Entity-Specific Details */}
            {followUp.entityType === "quote" && followUp.entityContext && (
              <Card sx={{ 
                mt: { xs: 2, md: 2 },
                boxShadow: { xs: 1, md: 2 },
              }}>
                <CardContent sx={{ 
                  p: { xs: 2, sm: 2.5, md: 3 },
                }}>
                  <Typography 
                    variant="h6" 
                    gutterBottom
                    sx={{
                      fontSize: { xs: "1.1rem", md: "1.25rem" },
                      fontWeight: 600,
                    }}
                  >
                    Quote Details
                  </Typography>
                  <Divider sx={{ mb: { xs: 1.5, md: 2 } }} />
                  <Grid container spacing={{ xs: 1.5, md: 2 }}>
                    {followUp.entityContext.from && (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          From
                        </Typography>
                        <Typography variant="body1">
                          {followUp.entityContext.from}
                        </Typography>
                      </Grid>
                    )}
                    {followUp.entityContext.to && (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          To
                        </Typography>
                        <Typography variant="body1">
                          {followUp.entityContext.to}
                        </Typography>
                      </Grid>
                    )}
                    {followUp.entityContext.dates?.length > 0 && (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          Dates
                        </Typography>
                        <Typography variant="body1">
                          {followUp.entityContext.dates.join(", ")}
                        </Typography>
                      </Grid>
                    )}
                    {followUp.entityContext.truckSize && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Truck Size
                        </Typography>
                        <Typography variant="body1">
                          {followUp.entityContext.truckSize.replace(/_/g, " ")}
                        </Typography>
                      </Grid>
                    )}
                    {followUp.entityContext.quoteType && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Quote Type
                        </Typography>
                        <Typography variant="body1">
                          {followUp.entityContext.quoteType}
                        </Typography>
                      </Grid>
                    )}
                    {followUp.entityContext.totalCost && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Total Cost
                        </Typography>
                        <Typography variant="body1" fontWeight={600}>
                          R{followUp.entityContext.totalCost.toLocaleString()}
                        </Typography>
                      </Grid>
                    )}
                    {followUp.entityContext.addOns && (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          Add-ons
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1,
                            flexWrap: "wrap",
                            mt: 0.5,
                          }}
                        >
                          {followUp.entityContext.addOns.boxes && (
                            <Chip label="Boxes" size="small" />
                          )}
                          {followUp.entityContext.addOns.bubbleWrap && (
                            <Chip label="Bubble Wrap" size="small" />
                          )}
                          {followUp.entityContext.addOns.protectiveBlankets && (
                            <Chip label="Protective Blankets" size="small" />
                          )}
                          {followUp.entityContext.addOns.packingService && (
                            <Chip label="Packing Service" size="small" />
                          )}
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            )}

            {followUp.entityType === "order" && followUp.entityContext && (
              <Card sx={{ 
                mt: { xs: 2, md: 2 },
                boxShadow: { xs: 1, md: 2 },
              }}>
                <CardContent sx={{ 
                  p: { xs: 2, sm: 2.5, md: 3 },
                }}>
                  <Typography 
                    variant="h6" 
                    gutterBottom
                    sx={{
                      fontSize: { xs: "1.1rem", md: "1.25rem" },
                      fontWeight: 600,
                    }}
                  >
                    Order Details
                  </Typography>
                  <Divider sx={{ mb: { xs: 1.5, md: 2 } }} />
                  <Grid container spacing={{ xs: 1.5, md: 2 }}>
                    {followUp.entityContext.pickupAddress && (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          Pickup Address
                        </Typography>
                        <Typography variant="body1">
                          {followUp.entityContext.pickupAddress}
                        </Typography>
                      </Grid>
                    )}
                    {followUp.entityContext.deliveryAddress && (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          Delivery Address
                        </Typography>
                        <Typography variant="body1">
                          {followUp.entityContext.deliveryAddress}
                        </Typography>
                      </Grid>
                    )}
                    {followUp.entityContext.moveDate && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Move Date
                        </Typography>
                        <Typography variant="body1">
                          {format(
                            new Date(followUp.entityContext.moveDate),
                            "MMM dd, yyyy"
                          )}
                        </Typography>
                      </Grid>
                    )}
                    {followUp.entityContext.pickupTimeWindow && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Pickup Time Window
                        </Typography>
                        <Typography variant="body1">
                          {followUp.entityContext.pickupTimeWindow}
                        </Typography>
                      </Grid>
                    )}
                    {followUp.entityContext.assignedDriver && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Assigned Driver
                        </Typography>
                        <Typography variant="body1">
                          {followUp.entityContext.assignedDriver}
                        </Typography>
                      </Grid>
                    )}
                    {followUp.entityContext.assignedVehicle && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Assigned Vehicle
                        </Typography>
                        <Typography variant="body1">
                          {followUp.entityContext.assignedVehicle}
                        </Typography>
                      </Grid>
                    )}
                    {followUp.entityContext.totalDistance && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Total Distance
                        </Typography>
                        <Typography variant="body1">
                          {followUp.entityContext.totalDistance} km
                        </Typography>
                      </Grid>
                    )}
                    {followUp.entityContext.amount && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Estimated Cost
                        </Typography>
                        <Typography variant="body1" fontWeight={600}>
                          R{followUp.entityContext.amount.toLocaleString()}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            )}

            {followUp.entityType === "lead" && followUp.entityContext && (
              <Card sx={{ 
                mt: { xs: 2, md: 2 },
                boxShadow: { xs: 1, md: 2 },
              }}>
                <CardContent sx={{ 
                  p: { xs: 2, sm: 2.5, md: 3 },
                }}>
                  <Typography 
                    variant="h6" 
                    gutterBottom
                    sx={{
                      fontSize: { xs: "1.1rem", md: "1.25rem" },
                      fontWeight: 600,
                    }}
                  >
                    Lead Details
                  </Typography>
                  <Divider sx={{ mb: { xs: 1.5, md: 2 } }} />
                  <Grid container spacing={{ xs: 1.5, md: 2 }}>
                    {followUp.entityContext.leadFrom && (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          From
                        </Typography>
                        <Typography variant="body1">
                          {followUp.entityContext.leadFrom}
                        </Typography>
                      </Grid>
                    )}
                    {followUp.entityContext.leadTo && (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          To
                        </Typography>
                        <Typography variant="body1">
                          {followUp.entityContext.leadTo}
                        </Typography>
                      </Grid>
                    )}
                    {followUp.entityContext.moveDate && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Move Date
                        </Typography>
                        <Typography variant="body1">
                          {format(
                            new Date(followUp.entityContext.moveDate),
                            "MMM dd, yyyy"
                          )}
                        </Typography>
                      </Grid>
                    )}
                    {followUp.entityContext.category && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Category
                        </Typography>
                        <Typography variant="body1">
                          {followUp.entityContext.category.replace(/_/g, " ")}
                        </Typography>
                      </Grid>
                    )}
                    {followUp.entityContext.budgetRange && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Budget Range
                        </Typography>
                        <Typography variant="body1">
                          {followUp.entityContext.budgetRange.replace(
                            /_/g,
                            " "
                          )}
                        </Typography>
                      </Grid>
                    )}
                    {followUp.entityContext.urgency && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Urgency
                        </Typography>
                        <Typography variant="body1">
                          {followUp.entityContext.urgency}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            )}

            {followUp.entityType === "invoice" && followUp.entityContext && (
              <Card sx={{ 
                mt: { xs: 2, md: 2 },
                boxShadow: { xs: 1, md: 2 },
              }}>
                <CardContent sx={{ 
                  p: { xs: 2, sm: 2.5, md: 3 },
                }}>
                  <Typography 
                    variant="h6" 
                    gutterBottom
                    sx={{
                      fontSize: { xs: "1.1rem", md: "1.25rem" },
                      fontWeight: 600,
                    }}
                  >
                    Invoice Details
                  </Typography>
                  <Divider sx={{ mb: { xs: 1.5, md: 2 } }} />
                  <Grid container spacing={{ xs: 1.5, md: 2 }}>
                    {followUp.entityContext.invoiceTotal && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Total Amount
                        </Typography>
                        <Typography variant="body1" fontWeight={600}>
                          R
                          {followUp.entityContext.invoiceTotal.toLocaleString()}
                        </Typography>
                      </Grid>
                    )}
                    {followUp.entityContext.dueDate && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Due Date
                        </Typography>
                        <Typography variant="body1">
                          {format(
                            new Date(followUp.entityContext.dueDate),
                            "MMM dd, yyyy"
                          )}
                        </Typography>
                      </Grid>
                    )}
                    {followUp.entityContext.itemsCount > 0 && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Items Count
                        </Typography>
                        <Typography variant="body1">
                          {followUp.entityContext.itemsCount} items
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            )}

            {followUp.entityType === "customer" && followUp.entityContext && (
              <Card sx={{ 
                mt: { xs: 2, md: 2 },
                boxShadow: { xs: 1, md: 2 },
              }}>
                <CardContent sx={{ 
                  p: { xs: 2, sm: 2.5, md: 3 },
                }}>
                  <Typography 
                    variant="h6" 
                    gutterBottom
                    sx={{
                      fontSize: { xs: "1.1rem", md: "1.25rem" },
                      fontWeight: 600,
                    }}
                  >
                    Customer Details
                  </Typography>
                  <Divider sx={{ mb: { xs: 1.5, md: 2 } }} />
                  <Grid container spacing={{ xs: 1.5, md: 2 }}>
                    {followUp.entityContext.customerAddress && (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          Address
                        </Typography>
                        <Typography variant="body1">
                          {followUp.entityContext.customerAddress}
                        </Typography>
                      </Grid>
                    )}
                    {(followUp.entityContext.leadsCount > 0 ||
                      followUp.entityContext.quotesCount > 0 ||
                      followUp.entityContext.ordersCount > 0) && (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          Activity Summary
                        </Typography>
                        <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
                          {followUp.entityContext.leadsCount > 0 && (
                            <Chip
                              label={`${followUp.entityContext.leadsCount} Leads`}
                              size="small"
                            />
                          )}
                          {followUp.entityContext.quotesCount > 0 && (
                            <Chip
                              label={`${followUp.entityContext.quotesCount} Quotes`}
                              size="small"
                            />
                          )}
                          {followUp.entityContext.ordersCount > 0 && (
                            <Chip
                              label={`${followUp.entityContext.ordersCount} Orders`}
                              size="small"
                            />
                          )}
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            )}

            {/* Customer Notes */}
            {followUp?.customerId && (
              <Box 
                mb={{ xs: 2, md: 3 }} 
                sx={{ mt: { xs: 2, md: 3 } }}
              >
                <ConfigProvider theme={bubakiiTheme}>
                  <CustomerNotesPanel
                    customerId={followUp.customerId}
                    context="followup"
                    relatedId={followUp.id}
                    entityType="followup"
                  />
                </ConfigProvider>
              </Box>
            )}
          </Grid>

          {/* Sidebar */}
          <Grid item xs={12} md={4}>
            <Box sx={{ 
              display: "flex", 
              flexDirection: "column", 
              gap: { xs: 2, md: 3 },
            }}>
              <Card sx={{ 
                boxShadow: { xs: 1, md: 2 },
              }}>
                <CardContent sx={{ 
                  p: { xs: 2, sm: 2.5, md: 3 },
                }}>
                  <Typography 
                    variant="h6" 
                    gutterBottom
                    sx={{
                      fontSize: { xs: "1.1rem", md: "1.25rem" },
                      fontWeight: 600,
                    }}
                  >
                    Information
                  </Typography>
                  <Divider sx={{ mb: { xs: 1.5, md: 2 } }} />

                  <Box
                    sx={{ 
                      display: "flex", 
                      flexDirection: "column", 
                      gap: { xs: 1.5, md: 2 },
                    }}
                  >
                    <Box>
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ 
                          fontSize: { xs: "0.75rem", md: "0.875rem" },
                          mb: 0.5,
                        }}
                      >
                        Status
                      </Typography>
                      <Chip
                        label={followUp.status}
                        color={getStatusColor(followUp.status)}
                        size={isMobile ? "small" : "medium"}
                        sx={{ 
                          textTransform: "capitalize",
                          fontSize: { xs: "0.75rem", md: "0.875rem" },
                        }}
                      />
                    </Box>

                    <Box>
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ 
                          fontSize: { xs: "0.75rem", md: "0.875rem" },
                          mb: 0.5,
                        }}
                      >
                        Priority
                      </Typography>
                      <Chip
                        label={followUp.priority}
                        color={getPriorityColor(followUp.priority)}
                        variant="outlined"
                        size={isMobile ? "small" : "medium"}
                        sx={{ 
                          textTransform: "capitalize",
                          fontSize: { xs: "0.75rem", md: "0.875rem" },
                        }}
                      />
                    </Box>

                    <Box>
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ 
                          fontSize: { xs: "0.75rem", md: "0.875rem" },
                          mb: 0.5,
                        }}
                      >
                        Scheduled Date
                      </Typography>
                      <Typography 
                        variant="body1"
                        sx={{
                          fontSize: { xs: "0.875rem", md: "1rem" },
                          wordBreak: "break-word",
                        }}
                      >
                        {followUp.scheduledDate
                          ? format(
                              new Date(followUp.scheduledDate),
                              "MMM dd, yyyy"
                            )
                          : "Not set"}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ 
                          fontSize: { xs: "0.75rem", md: "0.875rem" },
                          mb: 0.5,
                        }}
                      >
                        Due Date
                      </Typography>
                      <Typography 
                        variant="body1"
                        sx={{
                          fontSize: { xs: "0.875rem", md: "1rem" },
                          wordBreak: "break-word",
                        }}
                      >
                        {followUp.dueDate
                          ? format(new Date(followUp.dueDate), "MMM dd, yyyy")
                          : "Not set"}
                      </Typography>
                    </Box>

                    {followUp.completedAt && (
                      <Box>
                        <Typography 
                          variant="body2" 
                          color="text.secondary"
                          sx={{ 
                            fontSize: { xs: "0.75rem", md: "0.875rem" },
                            mb: 0.5,
                          }}
                        >
                          Completed At
                        </Typography>
                        <Typography 
                          variant="body1"
                          sx={{
                            fontSize: { xs: "0.875rem", md: "1rem" },
                            wordBreak: "break-word",
                          }}
                        >
                          {format(
                            new Date(followUp.completedAt),
                            "MMM dd, yyyy HH:mm"
                          )}
                        </Typography>
                      </Box>
                    )}

                    {followUp.assignedTo && (
                      <Box>
                        <Typography 
                          variant="body2" 
                          color="text.secondary"
                          sx={{ 
                            fontSize: { xs: "0.75rem", md: "0.875rem" },
                            mb: 0.5,
                          }}
                        >
                          Assigned To
                        </Typography>
                        <Typography 
                          variant="body1"
                          sx={{
                            fontSize: { xs: "0.875rem", md: "1rem" },
                            wordBreak: "break-word",
                          }}
                        >
                          {followUp.assignedTo}
                        </Typography>
                      </Box>
                    )}

                    {followUp.createdBy && (
                      <Box>
                        <Typography 
                          variant="body2" 
                          color="text.secondary"
                          sx={{ 
                            fontSize: { xs: "0.75rem", md: "0.875rem" },
                            mb: 0.5,
                          }}
                        >
                          Created By
                        </Typography>
                        <Typography 
                          variant="body1"
                          sx={{
                            fontSize: { xs: "0.875rem", md: "1rem" },
                            wordBreak: "break-word",
                          }}
                        >
                          {followUp.createdBy}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </Grid>
        </Grid>

        {/* Mobile Bottom Action Bar */}
        {isMobile && (
          <Paper
            elevation={8}
            sx={{
              position: "fixed",
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: 1200,
              px: 2,
              py: 1.25,
              borderRadius: "16px 16px 0 0",
              backdropFilter: "blur(6px)",
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              {followUp.status !== "completed" && (
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<CheckCircle />}
                  onClick={handleComplete}
                  fullWidth
                  size="small"
                >
                  Complete
                </Button>
              )}
              <Button
                variant="outlined"
                startIcon={<MoreVert />}
                fullWidth
                size="small"
                onClick={handleMenuOpen}
              >
                More
              </Button>
            </Stack>
          </Paper>
        )}

        {/* Template Modal */}
        <FollowUpTemplateSelector
          followUp={followUp}
          visible={templateModalVisible}
          onClose={() => setTemplateModalVisible(false)}
        />

        {/* Notification Snackbar */}
        <Snackbar
          open={notification.open}
          autoHideDuration={6000}
          onClose={() => setNotification({ ...notification, open: false })}
          anchorOrigin={{
            vertical: isMobile ? "bottom" : "bottom",
            horizontal: isMobile ? "center" : "right",
          }}
        >
          <Alert
            onClose={() => setNotification({ ...notification, open: false })}
            severity={notification.severity}
            sx={{ 
              width: { xs: "100%", sm: "auto" },
              maxWidth: { xs: "calc(100% - 32px)", sm: "400px" },
            }}
          >
            {notification.message}
          </Alert>
        </Snackbar>
      </Container>
    </ConfigProvider>
  );
};

export default FollowUpDetailView;
